import { groupBy } from "lodash";
import { startOfDay } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Unstable_Grid2/Grid2";
import TabPanel, { TabPanelProps } from "@mui/lab/TabPanel";
import {
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Typography,
} from "@mui/material";

import { FireworksIcon } from "@robin/common";

import { Badge } from "./primitives/Badge";
import { DateTime } from "./primitives/DateTime";
import { HorizontalStack } from "./primitives/Stacks";
import { PatientData } from "../models/patient-data";

export const ExperiencesTab: React.FC<
  TabPanelProps & { data: PatientData }
> = ({ data: { data }, ...tabPanelProps }) => {
  const { t } = useTranslation();

  const sortedExperiences = useMemo(() => {
    return data.experiences?.sort((a, b) => b.timestamp - a.timestamp) || [];
  }, [data]);

  const daysLoggedCount: number = useMemo(() => {
    if (!data.experiences) return 0;
    return (
      Object.keys(
        groupBy(data.experiences, (experience) =>
          startOfDay(new Date(experience.timestamp).getTime()),
        ),
      ).length || 0
    );
  }, [data]);

  if (!data) return null;

  const hasRecords = sortedExperiences && sortedExperiences.length > 0;

  return (
    <TabPanel {...tabPanelProps}>
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <List
          dense
          disablePadding
          subheader={
            <ListSubheader
              sx={{
                bgcolor: "background.default",
                borderBottom: "0.75px solid",
                borderColor: "divider",
                color: "text.primary",
                fontWeight: 500,
              }}
            >
              <HorizontalStack
                alignItems="center"
                justifyContent="space-between"
              >
                <HorizontalStack alignItems="center" flex="1 1 50%" spacing={1}>
                  <span>{t("tabs.experiences.experiences-label")}</span>
                  <Badge
                    label={data?.experiences?.length || 0}
                    size="small"
                    variant="outlined"
                  />
                </HorizontalStack>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    borderBottomWidth: "0.75px",
                    borderRightWidth: "0.75px",
                  }}
                />
                <HorizontalStack alignItems="center" flex="1 1 50%" spacing={1}>
                  <span>{t("tabs.experiences.days-count-label")}</span>
                  <Badge
                    label={daysLoggedCount}
                    size="small"
                    variant="outlined"
                  />
                </HorizontalStack>
              </HorizontalStack>
            </ListSubheader>
          }
        >
          {hasRecords ? (
            sortedExperiences.map((experience, num) => (
              <ListItem
                key={experience.id}
                divider={num < sortedExperiences?.length - 1}
              >
                <ListItemIcon>
                  <FireworksIcon
                    sx={{ color: "text.disabled" }}
                    fontSize="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <Grid
                        alignItems="center"
                        container
                        spacing={{ xs: 0.5, md: 2 }}
                      >
                        <Grid xs={12} md>
                          <Typography variant="body2">
                            {experience.title}
                          </Typography>
                        </Grid>
                        <Grid xs={12} md="auto">
                          <Typography
                            alignItems="center"
                            color="text.secondary"
                            display="flex"
                            sx={({ spacing }) => ({ gap: spacing(0.5) })}
                            variant="caption"
                          >
                            <DateTime timestamp={experience.timestamp} />
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  }
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText
                primary={t("tabs.experiences.no-data")}
                primaryTypographyProps={{
                  align: "center",
                  color: "text.secondary",
                }}
              />
            </ListItem>
          )}
        </List>
      </Paper>
    </TabPanel>
  );
};

export default ExperiencesTab;
